/**
 * Loan ID + export filename helpers (Phase A — client-side only).
 *
 *   Loan ID format:        FL-[Initials]-[NNNN]
 *   Export filename:       FL-[Initials]-[NNNN]-[YYYY-MM-DD]-[Amount]
 *
 * Sequential number is stored per-initials in localStorage until accounts
 * exist. Once we add per-user accounts the same helpers will be backed by
 * the database instead.
 */

const STORAGE_KEY = "flp.loanIdCounters.v1";

function readCounters(): Record<string, number> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function writeCounters(c: Record<string, number>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
}

/** First letter of first + last token of a name. Returns "" if no letters. */
function initialsFromName(name: string): string {
  const tokens = name
    .trim()
    .split(/\s+/)
    .map((t) => t.replace(/[^a-zA-Z]/g, ""))
    .filter(Boolean);
  if (tokens.length === 0) return "";
  if (tokens.length === 1) return tokens[0][0].toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

/**
 * Resolve client initials, preferring the loan borrower, then policy owner.
 * Falls back to "XX" so we always have a placeholder Loan ID before a name
 * is entered.
 */
export function resolveClientInitials(
  loanBorrower: string,
  policyOwner: string,
): string {
  return (
    initialsFromName(loanBorrower) ||
    initialsFromName(policyOwner) ||
    "XX"
  );
}

/** Peek at the current/next sequence number without incrementing it. */
export function peekNextLoanNumber(initials: string): number {
  const counters = readCounters();
  const current = counters[initials] ?? 0;
  return current + 1;
}

/** Reserve and persist the next loan number for these initials. */
export function reserveNextLoanNumber(initials: string): number {
  const counters = readCounters();
  const next = (counters[initials] ?? 0) + 1;
  counters[initials] = next;
  writeCounters(counters);
  return next;
}

/** Format a Loan ID from initials + numeric sequence. */
export function formatLoanId(initials: string, seq: number): string {
  const safeInitials = (initials || "XX").toUpperCase().slice(0, 4);
  const padded = String(Math.max(1, Math.floor(seq))).padStart(4, "0");
  return `FL-${safeInitials}-${padded}`;
}

/**
 * Build the export filename stem (no extension):
 *   FL-RC-0001-2026-06-24-84600
 * Amount is stripped of currency, commas and decimals.
 */
export function buildExportFilename(
  loanId: string,
  effectiveDateISO: string | null | undefined,
  totalLoanAmount: number,
): string {
  // Date: prefer effective date, else today
  let dateStr = "";
  if (effectiveDateISO && /^\d{4}-\d{2}-\d{2}/.test(effectiveDateISO)) {
    dateStr = effectiveDateISO.slice(0, 10);
  } else {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  const amount = Math.max(0, Math.round(Number(totalLoanAmount) || 0));
  return `${loanId}-${dateStr}-${amount}`;
}
